import { MFA_MAX_ATTEMPTS, MFA_PENDING_TTL_MS } from './mfa.constants';

export const MFA_PENDING_CHALLENGE_KEY = 'mfaPendingChallenge';

export interface PendingMfaChallenge {
  requestId: string;
  email: string;
  attempts: number;
  createdAt: number;
  codeSentAt: number;
}

function readRaw(): string | null {
  try {
    return sessionStorage.getItem(MFA_PENDING_CHALLENGE_KEY);
  } catch {
    return null;
  }
}

function normalizeCodeSentAt(codeSentAt: unknown, createdAt: number, now: number): number {
  if (typeof codeSentAt !== 'number' || !Number.isFinite(codeSentAt)) {
    return createdAt;
  }
  return codeSentAt >= createdAt && codeSentAt <= now ? codeSentAt : createdAt;
}

function normalizeAttempts(attempts: unknown): number {
  if (typeof attempts !== 'number' || !Number.isInteger(attempts)) {
    return 0;
  }
  return attempts >= 0 && attempts <= MFA_MAX_ATTEMPTS ? attempts : 0;
}

export function clearPendingChallenge(): void {
  try {
    sessionStorage.removeItem(MFA_PENDING_CHALLENGE_KEY);
  } catch {
    return;
  }
}

export function savePendingChallenge(challenge: PendingMfaChallenge): void {
  try {
    sessionStorage.setItem(MFA_PENDING_CHALLENGE_KEY, JSON.stringify(challenge));
  } catch {
    return;
  }
}

export function readPendingChallenge(now = Date.now()): PendingMfaChallenge | null {
  const raw = readRaw();

  if (raw === null) {
    return null;
  }

  let parsed: Partial<PendingMfaChallenge> | null;
  try {
    parsed = JSON.parse(raw) as Partial<PendingMfaChallenge> | null;
  } catch {
    clearPendingChallenge();
    return null;
  }

  if (!parsed || typeof parsed !== 'object') {
    clearPendingChallenge();
    return null;
  }

  const { requestId, email, createdAt } = parsed;

  const isUsable =
    typeof requestId === 'string' &&
    requestId.length > 0 &&
    typeof email === 'string' &&
    typeof createdAt === 'number' &&
    Number.isFinite(createdAt) &&
    createdAt <= now &&
    now - createdAt <= MFA_PENDING_TTL_MS;

  if (!isUsable) {
    clearPendingChallenge();
    return null;
  }

  return {
    requestId,
    email,
    attempts: normalizeAttempts(parsed.attempts),
    createdAt,
    codeSentAt: normalizeCodeSentAt(parsed.codeSentAt, createdAt, now),
  };
}
